import {
  INTRANET_CONTENT_KIND_LABELS,
  INTRANET_SEARCH_MIN_QUERY,
  IntranetSection,
  PermissionCode,
  intranetKnowledgeCategoryPath,
  intranetLibraryPath,
  type IntranetKnowledgeCategoryPageEntry,
  type IntranetKnowledgeHomeTile,
  type IntranetKnowledgeSearchHit,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { usePageTitle } from '@/hooks/use-page-title';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { ChannelIcon } from '@/products/intranet/components/intranet-channels';
import { KnowledgeMobileFilters } from '@/products/intranet/components/knowledge-mobile-filters';
import { KnowledgeNarrowing } from '@/products/intranet/components/knowledge-narrowing';
import { KnowledgeSiteFilter } from '@/products/intranet/components/knowledge-site-filter';
import {
  useIntranetKnowledgeCategoryFilter,
  useIntranetKnowledgeHome,
  useIntranetKnowledgeManufacturersReader,
  useIntranetKnowledgeSearch,
  useIntranetKnowledgeSites,
} from '@/products/intranet/features/intranet/use-intranet-knowledge';
import { useIntranetContents } from '@/products/intranet/features/intranet/use-intranet';
import {
  INTRANET_CHANNEL_COMPOSE_PATH,
  INTRANET_KNOWLEDGE_CATEGORIES_PATH,
  INTRANET_KNOWLEDGE_QUICK_ACCESS_PATH,
} from '@/products/intranet/intranet-paths';

export function LibraryListPage() {
  usePageTitle('Wissensdatenbank');
  const me = useMe();
  const canManageCategories = hasPermission(me.data, PermissionCode.INTRANET_CATEGORIES_MANAGE);
  const canManageQuickAccess = hasPermission(me.data, PermissionCode.INTRANET_CONTENT_MANAGE);
  const canCreatePage =
    hasPermission(me.data, PermissionCode.INTRANET_CONTENT_WRITE) || canManageQuickAccess;
  const [params, setParams] = useSearchParams();
  const [searchText, setSearchText] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const knowledgeSites = useIntranetKnowledgeSites();
  const categories = useIntranetKnowledgeCategoryFilter();
  const manufacturers = useIntranetKnowledgeManufacturersReader();
  const siteExplicit = params.has('standort');
  const siteId = siteExplicit
    ? (params.get('standort') ?? '')
    : (knowledgeSites.data?.preselectedSiteId ?? '');
  const kind = queryValue(params.get('art'));
  const categoryId = queryValue(params.get('kategorie'));
  const manufacturerId = queryValue(params.get('hersteller'));
  const home = useIntranetKnowledgeHome(siteId ? siteId : undefined);
  const narrowing = Boolean(kind ?? categoryId ?? manufacturerId);
  const drafts = useIntranetContents(
    {
      section: IntranetSection.LIBRARY,
      drafts: true,
      status: 'draft',
      page: 1,
      pageSize: 50,
    },
    canCreatePage,
  );
  const search = useIntranetKnowledgeSearch({
    q: activeQuery,
    siteId: siteId ? siteId : undefined,
    categoryId,
    kind: kind === 'guide' || kind === 'download' || kind === 'form' ? kind : undefined,
    manufacturerId,
  });

  function writeParam(key: string, value: string) {
    const updated = new URLSearchParams(params);
    if (value) {
      updated.set(key, value);
    } else {
      updated.delete(key);
    }
    setParams(updated, { replace: true });
  }

  function chooseSite(next: string) {
    const updated = new URLSearchParams(params);
    updated.set('standort', next);
    setParams(updated, { replace: true });
  }

  function submitSearch(event: FormEvent) {
    event.preventDefault();
    const next = searchText.trim();
    setActiveQuery(next.length >= INTRANET_SEARCH_MIN_QUERY ? next : '');
  }

  const categorySearch = new URLSearchParams(params);
  if (!params.has('standort') && siteId) {
    categorySearch.set('standort', siteId);
  }

  return (
    <section className="flex flex-col gap-5 sm:gap-8">
      <PlatformPageHeader
        title="Wissensdatenbank"
        description="Anleitungen, Downloads und Formulare zum Herunterladen."
        action={
          canCreatePage || canManageCategories || canManageQuickAccess ? (
            <div className="flex flex-wrap gap-1.5">
              {canCreatePage ? (
                <Button asChild size="sm">
                  <Link to={`${INTRANET_CHANNEL_COMPOSE_PATH}?kind=guide`}>Seite anlegen</Link>
                </Button>
              ) : null}
              {canManageQuickAccess ? (
                <Button asChild variant="outline" size="sm">
                  <Link to={INTRANET_KNOWLEDGE_QUICK_ACCESS_PATH}>Schnellzugriff pflegen</Link>
                </Button>
              ) : null}
              {canManageCategories ? (
                <Button asChild variant="outline" size="sm">
                  <Link to={INTRANET_KNOWLEDGE_CATEGORIES_PATH}>Kategorien</Link>
                </Button>
              ) : null}
            </div>
          ) : null
        }
      />

      <form
        role="search"
        onSubmit={submitSearch}
        className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-end"
      >
        <label className="flex min-w-0 flex-col gap-1 text-xs sm:col-span-2 sm:text-sm lg:min-w-48 lg:flex-1">
          <span className="text-muted-foreground">Suche</span>
          <Input
            type="search"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            className="h-8 w-full"
          />
        </label>
        <KnowledgeMobileFilters>
          <KnowledgeSiteFilter
            sites={knowledgeSites.data?.sites ?? []}
            siteId={siteId}
            onChange={chooseSite}
          />
          <KnowledgeNarrowing
            kind={kind}
            onKind={(value) => writeParam('art', value)}
            categoryId={categoryId}
            categories={categories.data?.items ?? []}
            onCategory={(value) => writeParam('kategorie', value)}
            manufacturerId={manufacturerId}
            manufacturers={manufacturers.data?.items ?? []}
            onManufacturer={(value) => writeParam('hersteller', value)}
          />
        </KnowledgeMobileFilters>
      </form>

      {activeQuery.length >= INTRANET_SEARCH_MIN_QUERY || narrowing ? (
        <SearchHits
          pending={search.isPending}
          error={search.isError}
          items={search.data?.items ?? []}
        />
      ) : null}

      {canCreatePage ? (
        <DraftRail
          pending={drafts.isPending}
          error={drafts.isError}
          items={drafts.data?.items ?? []}
        />
      ) : null}

      {home.isPending ? (
        <p className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Wissensdatenbank wird geladen …
        </p>
      ) : home.isError ? (
        <p role="alert" className="py-8 text-sm text-destructive">
          Die Wissensdatenbank konnte nicht geladen werden.
        </p>
      ) : (
        <div className="flex flex-col gap-8">
          {(home.data?.uncategorized ?? []).length > 0 ? (
            <Rail title="Ohne Kategorie">
              <PageLinks items={home.data?.uncategorized ?? []} />
            </Rail>
          ) : null}
          <Rail title="Themenkacheln">
            {(home.data?.tiles.length ?? 0) === 0 ? (
              <EmptyRail>Keine Themen.</EmptyRail>
            ) : (
              <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {home.data?.tiles.map((tile) => (
                  <li key={tile.id}>
                    <TileLink tile={tile} search={categorySearch.toString()} />
                  </li>
                ))}
              </ul>
            )}
          </Rail>
          <Rail title="Schnellzugriff">
            <PageLinks items={home.data?.quickAccess ?? []} />
          </Rail>
          <Rail title="Häufig genutzt">
            <PageLinks items={home.data?.frequentlyUsed ?? []} />
          </Rail>
          <Rail title="Zuletzt aktualisiert">
            <PageLinks items={home.data?.recentlyUpdated ?? []} />
          </Rail>
          <Rail title="Favoriten">
            <PageLinks items={home.data?.favorites ?? []} />
          </Rail>
        </div>
      )}
    </section>
  );
}

function queryValue(value: string | null): string | undefined {
  if (!value) {
    return undefined;
  }
  return value;
}

function Rail({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-primary">{title}</h2>
      {children}
    </section>
  );
}

function EmptyRail({ children }: { children: string }) {
  return (
    <p className="rounded-xl border border-dashed border-border bg-card px-3 py-4 text-center text-sm text-muted-foreground sm:px-4 sm:py-6">
      {children}
    </p>
  );
}

function DraftRail({
  pending,
  error,
  items,
}: {
  pending: boolean;
  error: boolean;
  items: readonly { id: string; title: string; slug: string }[];
}) {
  return (
    <Rail title="Entwürfe">
      {pending ? (
        <p className="text-sm text-muted-foreground">Entwürfe werden geladen …</p>
      ) : error ? (
        <p role="alert" className="text-sm text-destructive">
          Die Entwürfe konnten nicht geladen werden.
        </p>
      ) : items.length === 0 ? (
        <EmptyRail>Keine Entwürfe.</EmptyRail>
      ) : (
        <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {items.map((item) => (
            <li key={item.id}>
              <Link
                to={intranetLibraryPath(item.slug)}
                className="flex min-h-11 items-center rounded-xl border border-border bg-card px-4 py-3 font-semibold text-primary shadow-sm hover:border-primary focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:outline-none"
              >
                {item.title}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Rail>
  );
}

function TileLink({ tile, search }: { tile: IntranetKnowledgeHomeTile; search: string }) {
  return (
    <Link
      to={{ pathname: intranetKnowledgeCategoryPath(tile.slug), search }}
      className="flex min-h-11 items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 font-semibold text-primary shadow-sm hover:border-primary focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:outline-none"
    >
      <Badge variant="secondary">Kategorie</Badge>
      {tile.iconName ? <ChannelIcon name={tile.iconName} /> : null}
      {tile.name}
    </Link>
  );
}

function PageLinks({ items }: { items: readonly IntranetKnowledgeCategoryPageEntry[] }) {
  if (items.length === 0) {
    return <EmptyRail>Keine Seiten.</EmptyRail>;
  }
  return (
    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {items.map((item) => (
        <li key={item.id}>
          <Link
            to={intranetLibraryPath(item.slug)}
            className="flex h-full min-h-11 flex-col gap-2 rounded-xl border border-border bg-card p-4 shadow-sm hover:border-primary focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:outline-none"
          >
            <span className="flex flex-wrap gap-2">
              <Badge variant="secondary">Seite</Badge>
              <Badge variant="outline">{INTRANET_CONTENT_KIND_LABELS[item.kind]}</Badge>
            </span>
            <span className="font-semibold text-primary">{item.title}</span>
            {item.teaser ? (
              <span className="line-clamp-2 text-sm text-muted-foreground">{item.teaser}</span>
            ) : null}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function SearchHits({
  pending,
  error,
  items,
}: {
  pending: boolean;
  error: boolean;
  items: readonly IntranetKnowledgeSearchHit[];
}) {
  if (pending) {
    return <p className="text-sm text-muted-foreground">Suche läuft …</p>;
  }
  if (error) {
    return (
      <p role="alert" className="text-sm text-destructive">
        Die Suche ist fehlgeschlagen.
      </p>
    );
  }
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">Keine Treffer.</p>;
  }
  return (
    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {items.map((item) => (
        <li key={item.id}>
          <Link
            to={intranetLibraryPath(item.slug)}
            className="flex min-h-11 flex-col gap-1 rounded-xl border border-border bg-card px-4 py-3"
          >
            <span className="font-semibold text-primary">{item.title}</span>
            {item.teaser ? (
              <span className="line-clamp-2 text-sm text-muted-foreground">{item.teaser}</span>
            ) : null}
            {item.leadingCategory ? (
              <span className="text-sm text-muted-foreground">{item.leadingCategory.name}</span>
            ) : null}
          </Link>
        </li>
      ))}
    </ul>
  );
}
